import { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { AttendanceOvertimeRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { durationToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'

type AttendanceOvertimeRequestTaskContentProps = {
  id: string
  requesterContent: EmployeeTableViewRow
}

const AttendanceOvertimeRequestTaskContent: FC<AttendanceOvertimeRequestTaskContentProps> = ({
  id,
  requesterContent,
}) => {
  const { data } = useFragment({
    fragment: AttendanceOvertimeRequestApprovableFragmentDoc,
    fragmentName: 'AttendanceOvertimeRequestApprovable',
    from: {
      __typename: 'AttendanceOvertimeRequest',
      id,
    },
    optimistic: false,
  })

  if (!data) {
    return null
  }

  const date = data.date ? stringToDate(data.date, 'backend_datetime') : undefined

  const minutes =
    data.hoursAmountInCents !== undefined ? (data.hoursAmountInCents / 100) * 60 : undefined

  const displayDate = date ? i18nDateToString(date, 'ui_dmy_long') : ''

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    {
      title: i18n.t('time_tracking.modals.overtime_request.date'),
      content: {
        text: displayDate,
        type: 'item' as const,
      },
    },
    minutes && {
      title: i18n.t('time_tracking.modals.overtime_request.hours_amount'),
      content: {
        text: durationToText(minutes, 'minutes'),
        type: 'item' as const,
      },
    },
  ].filter(Boolean)

  return (
    <>
      <DetailsItemsList details={details} tableView />
      {!!data.description && (
        <NoteField
          label={i18n.t('time_tracking.modals.overtime_request.description_field')}
          content={data.description}
        />
      )}
      {!!data.reason && (
        <NoteField
          label={i18n.t('time_tracking.modals.overtime_request.additional_comments')}
          content={data.reason}
        />
      )}
    </>
  )
}

export default AttendanceOvertimeRequestTaskContent

export * from './metadata'
